import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BurnoutEpidemic = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Mental Health</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Why Burnout Affects More and More People
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>July 5, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>6 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/c1bf03f6-871e-48eb-87e6-22d0c017354f.png" 
              alt="Person working at computer with heavy boulder on their head representing the weight of burnout"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Burnout has become a defining issue of our time. Characterized by exhaustion, cynicism, and reduced performance, it no longer affects only high-stress professions like healthcare or education. Increasingly, people across all industries and age groups are reporting symptoms of burnout.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The scope of the problem</h2>

              <p>
                According to the World Health Organization, burnout is now officially recognized as an "occupational phenomenon." Surveys indicate that more than <strong className="text-accent">60% of employees globally</strong> report feeling burned out at least sometimes (Gallup, 2020). Among younger generations, the rates are even higher.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Causes of burnout</h2>

              <p>
                Burnout arises from chronic workplace stress that has not been successfully managed. Key contributors include:
              </p>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Overload of tasks</strong> and unrealistic expectations.</li>
                <li><strong className="text-accent">Lack of boundaries</strong> between work and home life.</li>
                <li><strong className="text-accent">Constant connectivity</strong> via smartphones and emails.</li>
                <li><strong className="text-accent">Insufficient support or recognition</strong> from leadership.</li>
              </ul>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The cost of burnout</h2>

              <p>
                Burnout is not just personal – it has economic costs. Gallup estimates that burnout contributes to nearly <strong className="text-accent">$300 billion annually in lost productivity</strong> worldwide. On an individual level, it increases the risk of depression, anxiety, and physical health problems such as cardiovascular disease (Melamed et al., 2006).
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Pathways to prevention</h2>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Setting clear boundaries</strong> between work and rest.</li>
                <li><strong className="text-accent">Encouraging workplaces</strong> to provide flexibility and mental health resources.</li>
                <li><strong className="text-accent">Practicing regular physical activity</strong> and mindfulness.</li>
                <li><strong className="text-accent">Taking restorative breaks</strong>, including digital detox periods.</li>
              </ul>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p>
                Burnout is not a personal failure but a systemic issue. Recognizing the signs early and creating environments that allow recovery are key steps in addressing this growing epidemic.
              </p>

              <p className="text-xl font-light italic text-accent">
                The weight we carry doesn't have to define us – healing is possible.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Gallup. (2020). Employee Burnout: Causes and Cures.</p>
                  <p>• World Health Organization. (2019). Burn-out an "occupational phenomenon": International Classification of Diseases.</p>
                  <p>• Melamed, S., Shirom, A., Toker, S., Berliner, S., & Shapira, I. (2006). Burnout and risk of cardiovascular disease: Evidence, possible causal paths, and promising research directions. <em>Psychological Bulletin, 132</em>(3), 327–353.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BurnoutEpidemic;